<template>
  <div class="operation-page">
    <!-- 统一容器：电站选择、时间选择、基础信息和收益信息 -->
    <div class="unified-container">
      <!-- 第一行：电站选择和时间选择 -->
      <div class="top-row-wrapper">
        <div class="top-row-container">
          <!-- 左边：电站选择 -->
          <div class="station-selector-wrapper">
            <StationSelector :current-station-id="currentStationId" @change="handleStationChange">
              <div class="station-name-row">
                <div class="station-name">{{ currentStationName || '请选择电站' }}</div>
                <div class="dropdown-icon">
                  <img :src="require('@/assets/images/station-monitor/image_67.png')" />
                </div>
              </div>
            </StationSelector>
          </div>

          <!-- 右边：时间选择和生成报告按钮 -->
          <div class="date-selector-wrapper">
            <el-date-picker v-model="selectedMonth" type="month" placeholder="选择月份" value-format="YYYY-MM"
              :clearable="false" class="date-picker" @change="handleMonthChange" />
            <el-button type="primary" class="generate-report-btn" @click="showGenerateReportDialog = true">
              <img :src="require('@/views/operation/assets/report.svg')" class="report-icon" />
              生成报告
            </el-button>
          </div>
        </div>
      </div>

      <!-- 第二行：基础信息和收益信息 -->
      <div class="info-row-container">
        <!-- 基础信息卡片 -->
        <div class="basic-info-card">
          <div class="basic-info-title">基础信息</div>
          <div class="basic-info-content">
            <div class="info-item">
              <span class="info-label">报告日期:</span>
              <span class="info-value">{{ basicInfo.reportNumber || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">装机容量:</span>
              <span class="info-value">{{ basicInfo.installedCapacity || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">场站名称:</span>
              <span class="info-value">{{ basicInfo.stationName || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">时间范围:</span>
              <span class="info-value">{{ basicInfo.timeRange || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">并网时间:</span>
              <span class="info-value">{{ basicInfo.gridConnectionTime || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">场站地址:</span>
              <span class="info-value">{{ basicInfo.stationAddress || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- 收益信息卡片 -->
        <div class="revenue-info-card">
          <div class="revenue-title-wrapper">
            <span class="revenue-info-title">收益信息</span>
            <div class="summary-suggestion" @click="showSummaryDialog = true">
              <span>总结建议</span>
              <v-icon icon="mdi-information-outline" size="small" class="info-icon"></v-icon>
            </div>
          </div>
          <div class="revenue-info-content">
            <div class="revenue-item">
              <div class="revenue-icon revenue-icon-red">
                <img :src="require('@/views/operation/assets/sum.svg')" alt="总收益" />
              </div>
              <div class="revenue-details">
                <div class="revenue-value">{{ revenueInfo.totalRevenue || '0' }}<span class="revenue-unit">万元</span>
                </div>
                <div class="revenue-label">总收益</div>
              </div>
            </div>
            <div class="revenue-item">
              <div class="revenue-icon revenue-icon-energy">
                <img :src="require('@/views/operation/assets/energy.svg')" alt="能量效率" />
              </div>
              <div class="revenue-details">
                <div class="revenue-value">{{ revenueInfo.energyEfficiency || '0' }}<span class="revenue-unit">%</span>
                </div>
                <div class="revenue-label">能量效率</div>
              </div>
            </div>
            <div class="revenue-item">
              <div class="revenue-icon revenue-icon-blue">
                <img :src="require('@/views/operation/assets/times.svg')" alt="等效循环次数" />
              </div>
              <div class="revenue-details">
                <div class="revenue-value">{{ revenueInfo.equivalentCycle || '0' }}<span class="revenue-unit">次</span>
                </div>
                <div class="revenue-label">等效循环次数</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="charts-container">
      <!-- 收益数据图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>收益数据</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('revenue')" />
          </div>
        </template>
        <RevenueChart ref="revenueChartRef" :data="revenueChartData" />
      </v-card>

      <!-- 充放电数据图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>充放电数据</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('charge')" />
          </div>
        </template>
        <ChargeDischargeChart ref="chargeDischargeChartRef" :data="chargeDischargeChartData" />
      </v-card>
    </div>

    <!-- 政策说明和电价信息 -->
    <div class="charts-container mt-4">
      <!-- 政策说明图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>政策说明</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('policy')" />
          </div>
        </template>
        <PolicyChart :data="policyChartData" />
      </v-card>

      <!-- 电价信息图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>电价信息</span>
          </div>
        </template>
        <PriceChart :data="priceChartData" />
      </v-card>
    </div>

    <!-- 告警统计图表 -->
    <div class="charts-container mt-4">
      <!-- 告警统计图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>告警统计</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('alarm')" />
          </div>
        </template>
        <AlarmStatisticsChart ref="alarmStatisticsChartRef" :data="alarmStatisticsChartData" />
      </v-card>

      <!-- 告警类别统计图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>告警类别统计</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('alarmCategory')" />
          </div>
        </template>
        <AlarmCategoryChart ref="alarmCategoryChartRef" :data="alarmCategoryChartData" />
      </v-card>
    </div>

    <!-- 负荷分析和效率分析图表 -->
    <div class="charts-container mt-4">
      <!-- 负荷分析图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>负荷分析</span>
            <img :src="require('@/assets/images/station-monitor/image_12.png')" class="expand-icon"
              @click="handleExpandChart('load')" />
          </div>
        </template>
        <LoadAnalysisChart ref="loadAnalysisChartRef" :data="loadAnalysisChartData" />
      </v-card>

      <!-- 效率分析图表 -->
      <v-card class="rounded-xl card-bg card-bd chart-card" variant="elevated" elevation="0">
        <template #title>
          <div class="chart-title-wrapper">
            <span>效率分析</span>
          </div>
        </template>
        <EfficiencyAnalysisChart :data="efficiencyAnalysisChartData" />
      </v-card>
    </div>

    <!-- 总结建议对话框 -->
    <v-dialog v-model="showSummaryDialog" :width="800" persistent class="summary-dialog">
      <v-card class="summary-dialog-card">
        <!-- 对话框标题栏 -->
        <div class="summary-dialog-header">
          <div class="summary-dialog-title">总结建议</div>
          <v-icon icon="mdi-close" class="close-icon" @click="showSummaryDialog = false"></v-icon>
        </div>

        <!-- 内容区域 -->
        <div class="summary-dialog-body">
          <div class="summary-dialog-content" v-html="generatedSummary"></div>
        </div>
      </v-card>
    </v-dialog>

    <!-- 生成报告对话框 -->
    <el-dialog v-model="showGenerateReportDialog" :show-close="false" width="90%" top="30px"
      :close-on-click-modal="false" class="generate-report-dialog" :lock-scroll="true">
      <div class="generate-report-dialog-content">
        <!-- 固定按钮区域 -->
        <div class="report-actions no-print">
          <v-btn color="primary" class="report-export-btn" @click="handleExportPDF">
            导出PDF
          </v-btn>
          <v-btn variant="outlined" class="report-return-btn" @click="handleCloseReportDialog">
            返回
          </v-btn>
        </div>
        <!-- 可滚动的内容区域 -->
        <div class="report-preview-wrapper">
          <!-- 报告预览内容 -->
          <div class="report-preview" ref="reportPreviewRef">
            <div class="report-header">
              <div class="report-title">{{ currentStationName }}运行报告</div>
            </div>
            <div class="report-content">
              <!-- 基础信息和收益信息 -->
              <div class="report-info-section">
                <div class="info-row">
                  <div class="basic-info-box">
                    <div class="section-title">基础信息</div>
                    <div class="info-content">
                      <div class="info-item">
                        <span class="info-label">报告编号：</span>
                        <span class="info-value">{{ basicInfo.reportNumber }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">装机容量：</span>
                        <span class="info-value">{{ basicInfo.installedCapacity }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">场站名称：</span>
                        <span class="info-value">{{ basicInfo.stationName }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">并网时间：</span>
                        <span class="info-value">{{ basicInfo.gridConnectionTime }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">时间范围：</span>
                        <span class="info-value">{{ basicInfo.timeRange }}</span>
                      </div>
                      <div class="info-item">
                        <span class="info-label">场站地址：</span>
                        <span class="info-value">{{ basicInfo.stationAddress }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="revenue-info-box">
                    <div class="section-title">收益信息</div>
                    <div class="revenue-content">
                      <div class="revenue-item">
                        <div class="revenue-icon-wrapper">
                          <img :src="require('@/views/operation/assets/sum.svg')" alt="总收益" />
                        </div>
                        <div class="revenue-details">
                          <div class="revenue-value">{{ revenueInfo.totalRevenue }}<span class="revenue-unit">万元</span>
                          </div>
                          <div class="revenue-label">总收益</div>
                        </div>
                      </div>
                      <div class="revenue-item">
                        <div class="revenue-icon-wrapper">
                          <img :src="require('@/views/operation/assets/energy.svg')" alt="能量效率" />
                        </div>
                        <div class="revenue-details">
                          <div class="revenue-value">{{ revenueInfo.energyEfficiency }}<span
                              class="revenue-unit">%</span>
                          </div>
                          <div class="revenue-label">能量效率</div>
                        </div>
                      </div>
                      <div class="revenue-item">
                        <div class="revenue-icon-wrapper">
                          <img :src="require('@/views/operation/assets/times.svg')" alt="等效循环次数" />
                        </div>
                        <div class="revenue-details">
                          <div class="revenue-value">{{ revenueInfo.equivalentCycle }}<span
                              class="revenue-unit">次</span>
                          </div>
                          <div class="revenue-label">等效循环次数</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- 总结建议 -->
              <div class="summary-section">
                <div class="section-title">总结建议</div>
                <div class="summary-content">
                  <div v-html="generatedSummary"></div>
                </div>
              </div>
              <!-- 图表区域 -->
              <div class="report-charts-section">
                <div class="charts-row">
                  <div class="chart-box">
                    <div class="chart-title">收益数据</div>
                    <RevenueChart :data="revenueChartData" />
                  </div>
                  <div class="chart-box">
                    <div class="chart-title">充放电数据</div>
                    <ChargeDischargeChart :data="chargeDischargeChartData" />
                  </div>
                </div>
                <div class="charts-row">
                  <div class="chart-box">
                    <div class="chart-title">政策说明</div>
                    <PolicyChart :data="policyChartData" />
                  </div>
                  <div class="chart-box">
                    <div class="chart-title">电价信息</div>
                    <PriceChart :data="priceChartData" />
                  </div>
                </div>
                <div class="charts-row">
                  <div class="chart-box">
                    <div class="chart-title">告警统计</div>
                    <AlarmStatisticsChart :data="alarmStatisticsChartData" />
                  </div>
                  <div class="chart-box">
                    <div class="chart-title">告警类别统计</div>
                    <AlarmCategoryChart ref="alarmCategoryChartRef" :data="alarmCategoryChartData" />
                  </div>
                </div>
                <div class="charts-row">
                  <div class="chart-box">
                    <div class="chart-title">负荷分析</div>
                    <LoadAnalysisChart :data="loadAnalysisChartData" />
                  </div>
                  <div class="chart-box">
                    <div class="chart-title">效率分析</div>
                    <EfficiencyAnalysisChart :data="efficiencyAnalysisChartData" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { ElMessage, ElLoading } from 'element-plus'
import useAppStore from '@/store/modules/app'
import StationSelector from '@/components/StationSelector/index.vue'
import RevenueChart from './components/RevenueChart.vue'
import ChargeDischargeChart from './components/ChargeDischargeChart.vue'
import PolicyChart from './components/PolicyChart.vue'
import PriceChart from './components/PriceChart.vue'
import AlarmStatisticsChart from './components/AlarmStatisticsChart.vue'
import AlarmCategoryChart from './components/AlarmCategoryChart.vue'
import LoadAnalysisChart from './components/LoadAnalysisChart.vue'
import EfficiencyAnalysisChart from './components/EfficiencyAnalysisChart.vue'
import { detail as getStationDetail } from '@/api/device/station'
import { getIncomeTotal, getIncomeByTime } from '@/api/production/overview'
import { getOperationCenterStatistics, getOperationCenterStandardRate, getLoadAnalysis } from '@/api/production/operationCenter'
import { queryElecPriceInUse } from '@/views/station-monitor/detail/components/elec-trovalency/api/index.js'

const route = useRoute()
const appStore = useAppStore()

// 当前选择的电站ID
const currentStationId = ref(null)
const currentStationName = ref('')

// 选择的月份
const selectedMonth = ref('')

// 生成报告对话框显示状态
const showGenerateReportDialog = ref(false)
// 总结建议对话框显示状态
const showSummaryDialog = ref(false)

// 报告预览内容的ref
const reportPreviewRef = ref(null)

// 图表组件的ref
const revenueChartRef = ref(null)
const chargeDischargeChartRef = ref(null)
const alarmStatisticsChartRef = ref(null)
const alarmCategoryChartRef = ref(null)
const loadAnalysisChartRef = ref(null)

// 基础信息
const basicInfo = reactive({
  reportNumber: '',
  timeRange: '',
  installedCapacity: '',
  gridConnectionTime: '',
  stationName: '',
  stationAddress: ''
})

// 收益信息
const revenueInfo = reactive({
  totalRevenue: '',
  energyEfficiency: '',
  equivalentCycle: '0'
})

// 初始化月份（默认当前月份）
const initMonth = () => {
  const today = dayjs()
  selectedMonth.value = today.format('YYYY-MM')
  updateBasicInfoTimeRange()
}

// 更新基础信息的时间范围
const updateBasicInfoTimeRange = () => {
  if (selectedMonth.value) {
    const month = dayjs(selectedMonth.value)
    const today = dayjs()
    const startOfMonth = month.startOf('month').format('YYYY年MM月DD日')

    // 如果是当前月，结束日期是今天，否则是月末
    let endDate
    if (month.isSame(today, 'month')) {
      endDate = today.format('YYYY年MM月DD日')
    } else {
      endDate = month.endOf('month').format('YYYY年MM月DD日')
    }

    basicInfo.timeRange = `${startOfMonth}-${endDate}`
  } else {
    basicInfo.timeRange = ''
  }
}

// 电站选择变化
const handleStationChange = (stationId) => {
  currentStationId.value = stationId
  appStore.setStationId(stationId)
  loadStationInfo(stationId)
}

// 月份变化
const handleMonthChange = async () => {
  updateBasicInfoTimeRange()
  // 月份变化时重新加载所有数据
  if (currentStationId.value) {
    await loadRevenueInfo()
    await loadChartData()
  }
}

// 更新基础信息
const updateBasicInfo = (stationData) => {
  if (!stationData) {
    resetBasicInfo()
    return
  }

  // 场站名称
  basicInfo.stationName = stationData.name || ''

  // 场站地址
  basicInfo.stationAddress = stationData.address || ''

  // 格式化并网时间
  if (stationData.commossioningTime) {
    basicInfo.gridConnectionTime = dayjs(stationData.commossioningTime).format('YYYY年MM月DD日')
  } else {
    basicInfo.gridConnectionTime = '-'
  }

  // 装机容量
  const maxPower = stationData.maxPower
  const capacity = stationData.capacity
  if (maxPower && capacity) {
    basicInfo.installedCapacity = `${maxPower}kW/${capacity}kWh`
  } else {
    basicInfo.installedCapacity = '-'
  }

  // 报告日期（当前日期）
  basicInfo.reportNumber = dayjs().format('YYYY年MM月DD日')
}

// 加载收益信息
const loadRevenueInfo = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，重置为默认值
    revenueInfo.totalRevenue = '0'
    revenueInfo.energyEfficiency = '0'
    revenueInfo.equivalentCycle = '0'
    return
  }

  // 根据选择的月份计算时间范围
  const month = dayjs(selectedMonth.value)
  const today = dayjs()

  const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')

  let endTime
  if (month.isSame(today, 'month')) {
    endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
  } else {
    endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
  }

  // 调用接口获取收益统计数据
  const response = await getIncomeTotal(
    currentStationId.value,
    startTime,
    endTime
  )

  // 从返回数据中提取总收益和能量效率
  const data = response.data || response

  if (data && typeof data === 'object') {
    const sumIncome = parseFloat(data.sumIncome || 0)
    revenueInfo.totalRevenue = (sumIncome / 10000).toFixed(2)

    const efficiency = parseFloat(data.efficiency || 0)
    revenueInfo.energyEfficiency = (efficiency * 100).toFixed(1)
  } else {
    revenueInfo.totalRevenue = '0'
    revenueInfo.energyEfficiency = '0'
  }

  // 等效循环次数固定为0
  revenueInfo.equivalentCycle = '0'
}

// 加载电站信息
const loadStationInfo = async (stationId) => {
  if (!stationId) {
    resetBasicInfo()
    return
  }

  // 调用接口获取电站详细信息
  const response = await getStationDetail(stationId)
  const stationData = response.data?.station

  if (stationData) {
    currentStationName.value = stationData.name || ''

    // 更新基础信息（包含报告日期）
    updateBasicInfo(stationData)

    // 更新时间范围
    updateBasicInfoTimeRange()

    // 加载收益信息
    loadRevenueInfo()
    // 加载图表数据
    loadChartData()
  } else {
    resetBasicInfo()
  }
}

// 重置基础信息
const resetBasicInfo = () => {
  basicInfo.reportNumber = ''
  basicInfo.installedCapacity = ''
  basicInfo.gridConnectionTime = ''
  basicInfo.stationName = ''
  basicInfo.stationAddress = ''
  basicInfo.timeRange = ''
  currentStationName.value = ''
  // 重置收益信息
  revenueInfo.totalRevenue = '0'
  revenueInfo.energyEfficiency = '0'
  revenueInfo.equivalentCycle = '0'
}


// 加载收益图表数据
const loadRevenueChartData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(revenueChartData, { categories: [], data: [] })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取收益数据
    const response = await getIncomeByTime({
      startTime,
      endTime,
      stationId: currentStationId.value,
      type: 'day'
    })

    // 处理返回数据
    const responseData = response.data

    // 使用 formatterData 格式化数据
    const { xAxis, dataList: formattedDataList } = formatterData(responseData, 'day')

    // 将日期格式化为 MM-DD（只显示月日）
    const categories = xAxis.map(date => {
      return dayjs(date).format('MM-DD')
    })

    // 提取收益数据
    const data = formattedDataList.map(item => {
      return item?.sum_income ? Number(parseFloat(item.sum_income).toFixed(2)) : null
    })

    Object.assign(revenueChartData, { categories, data })
  } catch (error) {
    console.error('加载收益数据失败:', error)
    // 出错时使用空数据
    Object.assign(revenueChartData, { categories: [], data: [] })
  }
}

// 加载告警统计数据
const loadAlarmStatisticsData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(alarmStatisticsChartData, { categories: [], data: [] })
    Object.assign(alarmCategoryChartData, { categories: [], data: [] })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取告警统计数据
    const response = await getOperationCenterStatistics({
      startTime,
      endTime,
      stationId: currentStationId.value
    })

    // 处理返回数据
    const responseData = response.data
    const statistics = responseData?.statistics || []
    const typeSort = responseData?.typeSort || []

    // 处理告警统计数据（statistics）
    // 使用 formatterData 函数来补全缺失的日期
    const { xAxis, dataList } = formatterData(statistics, 'day')

    // 将日期格式化为 MM-DD（只显示月日）
    const statisticsCategories = xAxis.map(date => {
      return dayjs(date).format('MM-DD')
    })

    // 提取告警数量数据
    const statisticsData = dataList.map(item => {
      return item?.count || 0
    })

    Object.assign(alarmStatisticsChartData, {
      categories: statisticsCategories,
      data: statisticsData
    })

    // 处理告警类别统计数据（typeSort）
    const categoryCategories = typeSort.map(item => item.description || '')
    const categoryData = typeSort.map(item => item.count || 0)

    Object.assign(alarmCategoryChartData, {
      categories: categoryCategories,
      data: categoryData
    })
  } catch (error) {
    console.error('加载告警统计数据失败:', error)
    // 出错时使用空数据
    Object.assign(alarmStatisticsChartData, { categories: [], data: [] })
    Object.assign(alarmCategoryChartData, { categories: [], data: [] })
  }
}

// 加载电价信息数据
const loadPriceChartData = async () => {
  if (!currentStationId.value) {
    // 如果没有电站ID，使用空数据
    Object.assign(priceChartData, {
      priceDetails: []
    })
    return
  }

  try {
    // 调用接口获取电价信息数据
    const response = await queryElecPriceInUse(currentStationId.value)

    // 处理返回数据
    const responseData = response.data
    const priceDetails = responseData[0]?.priceDetails || []

    Object.assign(priceChartData, {
      priceDetails: priceDetails || []
    })
  } catch (error) {
    console.error('加载电价信息数据失败:', error)
    // 出错时使用空数据
    Object.assign(priceChartData, {
      priceDetails: []
    })
  }
}

// 加载充放电图表数据
const loadChargeDischargeChartData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(chargeDischargeChartData, {
      categories: [],
      tableData: [],
      incomeModel: 1,
      sharpPeriodChargeElectricEnergy: [],
      peakPeriodChargeElectricEnergy: [],
      flatPeriodChargeElectricEnergy: [],
      valleyPeriodChargeElectricEnergy: [],
      deepValleyPeriodChargeElectricEnergy: [],
      sharpPeriodDischargeElectricEnergy: [],
      peakPeriodDischargeElectricEnergy: [],
      flatPeriodDischargeElectricEnergy: [],
      valleyPeriodDischargeElectricEnergy: [],
      deepValleyPeriodDischargeElectricEnergy: [],
      sum_e_positive: [],
      sum_e_reverse: []
    })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取充放电数据
    const response = await getIncomeByTime({
      startTime,
      endTime,
      stationId: currentStationId.value,
      type: 'day'
    })

    // 处理返回数据
    const responseData = response.data

    // 使用 formatterData 格式化数据
    const { xAxis, dataList: formattedDataList } = formatterData(responseData, 'day')

    // 将日期格式化为 MM-DD（只显示月日）
    const categories = xAxis.map(date => {
      return dayjs(date).format('MM-DD')
    })

    // 提取各种时段的数据
    const sharpPeriodChargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_positive_sharp || 0
    )
    const peakPeriodChargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_positive_peak || 0
    )
    const flatPeriodChargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_positive_flat || 0
    )
    const valleyPeriodChargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_positive_valley || 0
    )
    const deepValleyPeriodChargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_positive_deep_valley || 0
    )

    const sharpPeriodDischargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_reverse_sharp || 0
    )
    const peakPeriodDischargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_reverse_peak || 0
    )
    const flatPeriodDischargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_reverse_flat || 0
    )
    const valleyPeriodDischargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_reverse_valley || 0
    )
    const deepValleyPeriodDischargeElectricEnergy = formattedDataList.map(
      item => item?.sum_e_reverse_deep_valley || 0
    )

    const sum_e_positive = formattedDataList.map(
      item => item?.sum_e_positive || 0
    )
    const sum_e_reverse = formattedDataList.map(
      item => item?.sum_e_reverse || 0
    )

    Object.assign(chargeDischargeChartData, {
      categories,
      tableData: formattedDataList,
      incomeModel: 1, // 使用分时段显示模式
      sharpPeriodChargeElectricEnergy,
      peakPeriodChargeElectricEnergy,
      flatPeriodChargeElectricEnergy,
      valleyPeriodChargeElectricEnergy,
      deepValleyPeriodChargeElectricEnergy,
      sharpPeriodDischargeElectricEnergy,
      peakPeriodDischargeElectricEnergy,
      flatPeriodDischargeElectricEnergy,
      valleyPeriodDischargeElectricEnergy,
      deepValleyPeriodDischargeElectricEnergy,
      sum_e_positive,
      sum_e_reverse
    })
  } catch (error) {
    console.error('加载充放电数据失败:', error)
    // 出错时使用空数据
    Object.assign(chargeDischargeChartData, {
      categories: [],
      tableData: [],
      incomeModel: 1,
      sharpPeriodChargeElectricEnergy: [],
      peakPeriodChargeElectricEnergy: [],
      flatPeriodChargeElectricEnergy: [],
      valleyPeriodChargeElectricEnergy: [],
      deepValleyPeriodChargeElectricEnergy: [],
      sharpPeriodDischargeElectricEnergy: [],
      peakPeriodDischargeElectricEnergy: [],
      flatPeriodDischargeElectricEnergy: [],
      valleyPeriodDischargeElectricEnergy: [],
      deepValleyPeriodDischargeElectricEnergy: [],
      sum_e_positive: [],
      sum_e_reverse: []
    })
  }
}

// 将时间字符串转换为分钟数（用于排序和计算）
const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + (minutes || 0)
}

// 加载政策说明图表数据
const loadPolicyChartData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(policyChartData, {
      timeSegments: [],
      priceMap: {}
    })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取政策说明数据
    const response = await getOperationCenterStandardRate({
      startTime,
      endTime,
      stationId: currentStationId.value
    })

    // 处理返回数据
    const responseData = response.data
    const timeTable = responseData?.timeTable || []

    // 如果没有数据，使用空数据
    if (!timeTable || timeTable.length === 0) {
      Object.assign(policyChartData, {
        timeSegments: [],
        priceMap: {}
      })
      return
    }

    const selectedRule = timeTable[0]
    const ruleInfo = selectedRule?.ruleInfo || {}

    // 时段配置：名称、颜色、电价（从priceChartData获取）
    // 颜色与电价信息保持一致
    const periodConfig = {
      flat: { name: '平时', color: '#10B981', colorLight: 'rgba(16, 185, 129, 0.3)' },
      valley: { name: '谷时', color: '#3A68FF', colorLight: 'rgba(58, 104, 255, 0.3)' },
      deepValley: { name: '深谷', color: '#FFD700', colorLight: 'rgba(255, 215, 0, 0.3)' },
      sharp: { name: '尖峰', color: '#FF6B6B', colorLight: 'rgba(255, 107, 107, 0.3)' },
      peak: { name: '高峰', color: '#FF7A45', colorLight: 'rgba(255, 122, 69, 0.3)' }
    }

    // 获取电价信息（用于tooltip显示）
    const priceMap = {}
    if (priceChartData.priceDetails && priceChartData.priceDetails.length > 0) {
      priceChartData.priceDetails.forEach(item => {
        if (item.name && periodConfig[item.name]) {
          priceMap[item.name] = Number(item.price) || 0
        }
      })
    }

    // 解析时间段，转换为24小时轴上的分段
    const timeSegments = []

    // 遍历所有时段类型
    Object.keys(periodConfig).forEach(periodType => {
      const periods = ruleInfo[periodType] || []
      const config = periodConfig[periodType]

      periods.forEach(([startTime, endTime]) => {
        const startMinutes = timeToMinutes(startTime)
        let endMinutes = timeToMinutes(endTime)

        // 如果结束时间是24:00，转换为1440分钟（24小时）
        if (endTime === '24:00' || endTime === '24:00:00') {
          endMinutes = 1440
        }

        timeSegments.push({
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          periodType,
          periodName: config.name,
          color: config.color,
          colorLight: config.colorLight,
          price: priceMap[periodType] || 0
        })
      })
    })

    // 按开始时间排序
    timeSegments.sort((a, b) => a.startMinutes - b.startMinutes)

    // 生成24小时时间轴（每小时一个点）
    const timeAxis = []
    for (let hour = 0; hour <= 24; hour++) {
      timeAxis.push(`${hour.toString().padStart(2, '0')}:00`)
    }

    Object.assign(policyChartData, {
      timeSegments,
      timeAxis,
      priceMap
    })
  } catch (error) {
    console.error('加载政策说明数据失败:', error)
    // 出错时使用空数据
    Object.assign(policyChartData, {
      timeSegments: [],
      timeAxis: [],
      priceMap: {}
    })
  }
}

// 加载负荷分析图表数据
const loadLoadAnalysisChartData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(loadAnalysisChartData, {
      categories: [],
      seriesData: []
    })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取负荷分析数据
    const response = await getLoadAnalysis({
      startTime,
      endTime,
      stationId: currentStationId.value
    })

    // 处理返回数据
    const responseData = response.data
    const dataList = Array.isArray(responseData) ? responseData : (responseData?.data || [])

    // 如果没有数据，返回空数据
    if (!dataList || dataList.length === 0) {
      Object.assign(loadAnalysisChartData, {
        categories: [],
        seriesData: []
      })
      return
    }

    // 收集所有时间点（用于X轴）
    const allTimePoints = new Set()
    dataList.forEach(meter => {
      if (meter.powerList && Array.isArray(meter.powerList)) {
        meter.powerList.forEach(power => {
          if (power.record_time) {
            allTimePoints.add(power.record_time)
          }
        })
      }
    })

    // 将时间点排序
    const sortedTimePoints = Array.from(allTimePoints).sort((a, b) => {
      return new Date(a) - new Date(b)
    })

    // 格式化时间点作为X轴数据
    const categories = sortedTimePoints.map(time => {
      return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    })

    // 为每个电表生成数据系列
    const seriesData = dataList.map(meter => {
      // 创建时间到功率值的映射
      const powerMap = {}
      if (meter.powerList && Array.isArray(meter.powerList)) {
        meter.powerList.forEach(power => {
          if (power.record_time) {
            powerMap[power.record_time] = power.p_total || 0
          }
        })
      }

      // 根据时间点生成数据数组
      const data = sortedTimePoints.map(time => {
        return powerMap[time] !== undefined ? Number(powerMap[time]) : null
      })

      return {
        name: meter.name || meter.uuid || '未知电表',
        uuid: meter.uuid,
        data: data
      }
    })

    Object.assign(loadAnalysisChartData, {
      categories,
      seriesData
    })
  } catch (error) {
    console.error('加载负荷分析数据失败:', error)
    // 出错时使用空数据
    Object.assign(loadAnalysisChartData, {
      categories: [],
      seriesData: []
    })
  }
}

// 加载效率分析图表数据
const loadEfficiencyAnalysisChartData = async () => {
  if (!currentStationId.value || !selectedMonth.value) {
    // 如果没有电站ID或月份，使用空数据
    Object.assign(efficiencyAnalysisChartData, {
      month: selectedMonth.value || dayjs().format('YYYY-MM'),
      efficiencyData: {}
    })
    return
  }

  try {
    // 根据选择的月份计算时间范围
    const month = dayjs(selectedMonth.value)
    const today = dayjs()

    const startTime = month.startOf('month').format('YYYY-MM-DD HH:mm:ss')
    let endTime
    if (month.isSame(today, 'month')) {
      endTime = today.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      endTime = month.endOf('month').format('YYYY-MM-DD HH:mm:ss')
    }

    // 调用接口获取充放电数据
    const response = await getIncomeByTime({
      startTime,
      endTime,
      stationId: currentStationId.value,
      type: 'day'
    })

    // 处理返回数据
    const responseData = response.data || response
    let dataList = Array.isArray(responseData) ? responseData : (responseData?.data || [])

    // 使用 formatterData 格式化数据
    const { xAxis, dataList: formattedDataList } = formatterData(dataList, 'day')

    // 构建效率数据对象，效率 = 放电/充电 * 100%
    const efficiencyData = {}
    xAxis.forEach((date, index) => {
      const item = formattedDataList[index]
      if (item) {
        const charge = parseFloat(item.sum_e_positive || 0) // 充电
        const discharge = parseFloat(item.sum_e_reverse || 0) // 放电

        // 计算效率：放电/充电 * 100%
        let efficiency = 0
        if (charge > 0) {
          efficiency = (discharge / charge) * 100
        }

        // 保留两位小数
        efficiencyData[date] = parseFloat(efficiency.toFixed(2))
      }
    })

    Object.assign(efficiencyAnalysisChartData, {
      month: selectedMonth.value,
      efficiencyData
    })
  } catch (error) {
    console.error('加载效率分析数据失败:', error)
    // 出错时使用空数据
    Object.assign(efficiencyAnalysisChartData, {
      month: selectedMonth.value || dayjs().format('YYYY-MM'),
      efficiencyData: {}
    })
  }
}

// 格式化数据函数
const formatterData = (data, type = 'day') => {
  if (!selectedMonth.value) return { xAxis: [], dataList: [] }

  const month = dayjs(selectedMonth.value)
  const today = dayjs()

  let startTime = month.startOf('month')
  let endTime
  if (month.isSame(today, 'month')) {
    endTime = today.endOf('day')
  } else {
    endTime = month.endOf('month')
  }

  const templates = {
    day: "YYYY-MM-DD",
    month: "YYYY-MM",
    year: "YYYY"
  }
  const tpl = templates[type]
  const findByKey = (list, key, value) => {
    return list.find(item => item[key] === value)
  }

  const xAxis = []
  const dataList = []

  while (startTime.isBefore(endTime) || startTime.isSame(endTime, 'day')) {
    const date = startTime.format(tpl)
    const item = findByKey(data, "date", date)

    xAxis.push(date)
    dataList.push(item)
    startTime = startTime.add(1, type)
  }

  return {
    xAxis,
    dataList
  }
}

// 图表数据
const revenueChartData = reactive({ categories: [], data: [] })
const chargeDischargeChartData = reactive({
  categories: [],
  tableData: [],
  incomeModel: 1,
  sharpPeriodChargeElectricEnergy: [],
  peakPeriodChargeElectricEnergy: [],
  flatPeriodChargeElectricEnergy: [],
  valleyPeriodChargeElectricEnergy: [],
  deepValleyPeriodChargeElectricEnergy: [],
  sharpPeriodDischargeElectricEnergy: [],
  peakPeriodDischargeElectricEnergy: [],
  flatPeriodDischargeElectricEnergy: [],
  valleyPeriodDischargeElectricEnergy: [],
  deepValleyPeriodDischargeElectricEnergy: [],
  sum_e_positive: [],
  sum_e_reverse: []
})
const policyChartData = reactive({
  timeSegments: [],
  timeAxis: [],
  priceMap: {}
})
const priceChartData = reactive({ priceDetails: [] })
const alarmStatisticsChartData = reactive({ categories: [], data: [] })
const alarmCategoryChartData = reactive({ categories: [], data: [] })
const loadAnalysisChartData = reactive({
  categories: [],
  seriesData: [] // 每个电表的数据系列，格式: [{ name: '电表名称', data: [功率值数组] }]
})
const efficiencyAnalysisChartData = reactive({
  month: selectedMonth.value || dayjs().format('YYYY-MM'),
  efficiencyData: {}
})

// 加载图表数据
const loadChartData = async () => {
  // 加载收益图表数据
  await loadRevenueChartData()
  // 加载充放电图表数据
  await loadChargeDischargeChartData()
  // 加载告警统计数据
  await loadAlarmStatisticsData()
  // 加载电价信息数据（先加载，供政策说明使用）
  await loadPriceChartData()
  // 加载政策说明数据（需要在电价信息之后加载）
  await loadPolicyChartData()
  // 加载效率分析数据
  await loadEfficiencyAnalysisChartData()
  // 加载负荷分析数据
  await loadLoadAnalysisChartData()
}

// 生成总结建议内容
const generatedSummary = computed(() => {
  if (!currentStationId.value || !selectedMonth.value) {
    return '<p>暂无数据</p>'
  }

  const month = dayjs(selectedMonth.value)
  const today = dayjs()
  const startDate = month.startOf('month')
  const endDate = month.isSame(today, 'month') ? today : month.endOf('month')

  // 格式化时间范围
  const timeRange = `${startDate.format('YYYY年MM月DD日')}~${endDate.format('YYYY年MM月DD日')}`
  const stationName = currentStationName.value || '电站'

  // 1. 电站整体收益（展示万元与约等的元）
  const revenueWan = revenueInfo.totalRevenue || '0'
  const totalRevenueDisplay = `${revenueWan} 万元`
  const equivalentCycle = revenueInfo.equivalentCycle || '0'

  // 查找最高收益日期（基于收益图表数据）
  let maxRevenueDate = ''
  let maxRevenueCharge = null
  let maxRevenueDischarge = null
  if (Array.isArray(revenueChartData.categories) && Array.isArray(revenueChartData.data) && revenueChartData.data.length > 0) {
    const validData = revenueChartData.data.filter(v => v !== null && v !== undefined)
    if (validData.length > 0) {
      const maxRevenue = Math.max(...validData)
      const maxRevenueIndex = revenueChartData.data.findIndex(v => v === maxRevenue)
      if (maxRevenueIndex >= 0) {
        const dateStr = revenueChartData.categories[maxRevenueIndex]
        const parsed = dayjs(`${selectedMonth.value}-${dateStr}`)
        maxRevenueDate = parsed.isValid() ? parsed.format('YYYY年MM月DD日') : dateStr

        // 获取对应日期的充放电数据
        if (Array.isArray(chargeDischargeChartData.sum_e_positive) && chargeDischargeChartData.sum_e_positive[maxRevenueIndex] !== undefined) {
          maxRevenueCharge = parseFloat(chargeDischargeChartData.sum_e_positive[maxRevenueIndex])
        }
        if (Array.isArray(chargeDischargeChartData.sum_e_reverse) && chargeDischargeChartData.sum_e_reverse[maxRevenueIndex] !== undefined) {
          maxRevenueDischarge = parseFloat(chargeDischargeChartData.sum_e_reverse[maxRevenueIndex])
        }
      }
    }
  }

  // 2. 告警统计：最多告警的日期与最多类型
  let maxAlarmDate = ''
  let maxAlarmType = ''
  if (Array.isArray(alarmStatisticsChartData.categories) && Array.isArray(alarmStatisticsChartData.data) && alarmStatisticsChartData.data.length > 0) {
    const validAlarmData = alarmStatisticsChartData.data.filter(v => v !== null && v !== undefined)
    if (validAlarmData.length > 0) {
      const maxAlarmCount = Math.max(...validAlarmData)
      const maxAlarmIndex = alarmStatisticsChartData.data.findIndex(v => v === maxAlarmCount)
      if (maxAlarmIndex >= 0) {
        const dateStr = alarmStatisticsChartData.categories[maxAlarmIndex]
        const parsed = dayjs(`${selectedMonth.value}-${dateStr}`)
        maxAlarmDate = parsed.isValid() ? parsed.format('YYYY年MM月DD日') : dateStr
      }
    }
  }
  if (Array.isArray(alarmCategoryChartData.categories) && Array.isArray(alarmCategoryChartData.data) && alarmCategoryChartData.data.length > 0) {
    const maxCategoryCount = Math.max(...alarmCategoryChartData.data)
    const maxCategoryIndex = alarmCategoryChartData.data.findIndex(v => v === maxCategoryCount)
    if (maxCategoryIndex >= 0) {
      maxAlarmType = alarmCategoryChartData.categories[maxCategoryIndex] || '未知告警'
    }
  }

  // 3. 运行效率区间与出现最大值的日期（基于效率分析数据）
  const efficiencyMap = efficiencyAnalysisChartData.efficiencyData || {}
  // 过滤出非空且在0-100范围内的值
  const efficiencyEntries = Object.entries(efficiencyMap).filter(([, v]) => {
    if (v === null || v === undefined) return false
    const value = Number(v)
    return value >= 0 && value <= 100
  })
  let efficiencyMin = null
  let efficiencyMax = null
  let maxEfficiencyDates = []
  if (efficiencyEntries.length > 0) {
    const values = efficiencyEntries.map(([, v]) => Number(v))
    efficiencyMin = Math.min(...values)
    efficiencyMax = Math.max(...values)
    // 找出所有等于最大值的日期
    maxEfficiencyDates = efficiencyEntries.filter(([, v]) => Number(v) === efficiencyMax).map(([date]) => {
      const parsed = dayjs(date)
      return parsed.isValid() ? parsed.format('YYYY年MM月DD日') : date
    })
  }

  // 生成内容并做安全兜底
  const revenuePart = `1. 电站整体收益：${totalRevenueDisplay}，等效循环次数：${equivalentCycle}次${maxRevenueDate ? `；最高收益出现在${maxRevenueDate}${maxRevenueCharge !== null ? `，当天充电量 ${maxRevenueCharge.toFixed(2)} kWh，放电量 ${maxRevenueDischarge.toFixed(2)} kWh` : ''}` : ''}`
  const alarmPart = `2. 告警统计：告警最多的日期为 ${maxAlarmDate || '无数据'}，告警类型最多为${maxAlarmType || '无数据'}`
  const effRange = (efficiencyMin === null || efficiencyMax === null) ? '无数据' : `${efficiencyMin.toFixed(2)}% - ${efficiencyMax.toFixed(2)}%`
  const effMaxPart = maxEfficiencyDates.length > 0 ? `，效率最大值出现在${maxEfficiencyDates.join('、')}` : ''
  const efficiencyPart = `3. 运行效率：整体集中在${effRange}${effMaxPart}这天`

  const summary = `
    <p>基于所选时间（${timeRange}），${stationName}整体运行状态如下：</p>
    <p>${revenuePart}</p>
    <p>${alarmPart}</p>
    <p>${efficiencyPart}</p>
  `

  return summary
})

// 处理图表全屏
const handleExpandChart = (type) => {
  if (type === 'revenue' && revenueChartRef.value) {
    revenueChartRef.value.openFullscreen()
  } else if (type === 'charge' && chargeDischargeChartRef.value) {
    chargeDischargeChartRef.value.openFullscreen()
  } else if (type === 'alarm' && alarmStatisticsChartRef.value) {
    alarmStatisticsChartRef.value.openFullscreen()
  } else if (type === 'alarmCategory' && alarmCategoryChartRef.value) {
    alarmCategoryChartRef.value.openFullscreen()
  } else if (type === 'load' && loadAnalysisChartRef.value) {
    loadAnalysisChartRef.value.openFullscreen()
  }
}

// 导出PDF
const handleExportPDF = async () => {
  if (!reportPreviewRef.value) {
    console.error('报告预览内容不存在')
    return
  }

  try {
    // 显示加载提示
    const loading = ElLoading.service({
      lock: true,
      text: '正在生成PDF，请稍候...',
      background: 'rgba(0, 0, 0, 0.7)'
    })

    // 临时隐藏按钮
    const noPrintElements = reportPreviewRef.value.querySelectorAll('.no-print')
    const originalDisplay = []
    noPrintElements.forEach((el, index) => {
      originalDisplay[index] = el.style.display
      el.style.display = 'none'
    })

    // 使用html2canvas将内容转换为canvas
    const canvas = await html2canvas(reportPreviewRef.value, {
      scale: 2, // 提高清晰度
      useCORS: true, // 允许跨域图片
      logging: false, // 关闭日志
      backgroundColor: '#ffffff' // 设置背景色
    })

    // 恢复按钮显示
    noPrintElements.forEach((el, index) => {
      el.style.display = originalDisplay[index] || ''
    })

    // 获取canvas的宽高
    const imgWidth = canvas.width
    const imgHeight = canvas.height

    // A4纸的尺寸（单位：mm）
    const pdfWidth = 210
    const pdfHeight = 297

    // 计算缩放比例，确保内容能完整显示
    const ratio = pdfWidth / imgWidth
    const scaledHeight = imgHeight * ratio

    // 创建PDF实例
    const pdf = new jsPDF('p', 'mm', 'a4')

    // 如果内容高度超过一页，需要分页
    let heightLeft = scaledHeight
    let position = 0

    // 添加第一页
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, pdfWidth, scaledHeight)
    heightLeft -= pdfHeight

    // 生成文件名
    const fileName = `${currentStationName.value}运行报告_${dayjs().format('YYYY-MM-DD')}.pdf`

    // 保存PDF
    pdf.save(fileName)

    // 关闭加载提示
    loading.close()
    ElMessage.success('PDF导出成功')
  } catch (error) {
    console.error('导出PDF失败:', error)
    ElMessage.error('导出PDF失败，请重试')

    // 出错也恢复按钮显示
    if (reportPreviewRef.value) {
      const noPrintElements = reportPreviewRef.value.querySelectorAll('.no-print')
      noPrintElements.forEach((el) => {
        el.style.display = ''
      })
    }
  }
}

// 关闭报告对话框
const handleCloseReportDialog = () => {
  showGenerateReportDialog.value = false
}

onMounted(() => {
  // 初始化月份
  initMonth()

  // 如果store中没有电站列表，获取电站列表
  if (!appStore.stations || appStore.stations.length === 0) {
    appStore.getStations()
  }

  // 如果有路由参数中的stationId，使用它
  const routeStationId = route.query.stationId
  if (routeStationId) {
    currentStationId.value = routeStationId
    appStore.setStationId(routeStationId)
    // 延迟加载，等待stations加载完成
    setTimeout(() => {
      loadStationInfo(routeStationId)
    }, 100)
  } else if (appStore.stationId) {
    // 否则使用store中的stationId
    currentStationId.value = appStore.stationId
    // 延迟加载，等待stations加载完成
    setTimeout(() => {
      loadStationInfo(appStore.stationId)
    }, 100)
  }
})

// 当打开生成报告对话框时，确保报告内的图表使用最新的同一份数据并触发 resize
watch(() => showGenerateReportDialog.value, async (val) => {
  if (val) {
    await loadChartData()
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
  }
})
</script>

<style scoped lang="scss">
.operation-page {
  padding: 0;
  margin: 0 20px 20px 0;
  overflow: auto;

  // 隐藏滚动条
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE 10+

  &::-webkit-scrollbar {
    display: none; // Chrome, Safari, Edge
  }

  :deep(.v-card-item) {
    padding: 24px 28px 10px 28px !important;

    .v-card-title {
      font-weight: 400 !important;
      font-size: 20px !important;
    }
  }
}

.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.unified-container {
  padding: 20px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 102.42%);
  border: 1px solid #fff;
  margin-bottom: 16px;
}

.top-row-wrapper {
  margin-bottom: 16px;
}

.top-row-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.station-selector-wrapper {
  flex-shrink: 0;

  .station-name-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .station-name {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 24px;
    color: rgba(46, 46, 46, 1);
    font-weight: 500;
    line-height: normal;
  }

  .dropdown-icon {
    margin-top: 8px;
    width: 11px;
    height: 11px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 9.63px;
      height: 6.19px;
    }
  }
}

.date-selector-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  :deep(.date-picker) {
    height: 40px;
    width: 180px;

    .el-input__wrapper {
      border-radius: 16px;
      border: 1px solid #D4D9E5;
    }
  }

  .generate-report-btn {
    color: #fff;
    background: #3A68FF;
    border-color: #3A68FF;
    height: 40px;
    padding: 0 24px;
    border-radius: 16px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;

    .report-icon {
      width: 16px;
      height: 17px;
      margin-right: 6px;
      flex-shrink: 0;
    }
  }
}

// 公共卡片样式
.basic-info-card,
.revenue-info-card {
  min-width: 0;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
  border: 1px solid #fff;
}

.basic-info-card {
  padding: 24px 24px 0px 24px;
}

.revenue-info-card {
  flex-grow: 1;
  padding: 24px 28px;
}

// 公共标题样式
.basic-info-title,
.revenue-info-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #2E2E2E;
}

.basic-info-title {
  padding-bottom: 16px;
}

.revenue-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 16px;

  .summary-suggestion {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    color: #3A68FF;
    cursor: pointer;

    .info-icon {
      color: #3A68FF;
      font-size: 16px;
    }
  }
}

.basic-info-content {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  padding: 20px 0;
}

.info-item {
  display: flex;
  align-items: flex-start;
  min-height: 24px;

  .info-label {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0%;
    color: rgba(46, 46, 46, 0.698); // #2E2E2EB2
    margin-right: 8px;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .info-value {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: #2e2e2eb2;
    white-space: nowrap;
  }
}

.info-row-container {
  display: flex;
  gap: 16px;
}

.revenue-info-content {
  display: flex;
  justify-content: space-around;
  gap: 32px;
  padding: 20px 0;
}

.revenue-item {
  display: flex;
  align-items: center;
  gap: 16px;
}

.revenue-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    font-size: 24px;
    font-weight: 600;
    color: #fff;
  }

  img {
    width: 32px;
    height: 35px;
    object-fit: contain;
  }

  &.revenue-icon-energy img {
    width: 40px;
    height: 44px;
  }

  &.revenue-icon-red img {
    width: 40px;
    height: 44px;
  }

  &.revenue-icon-blue img {
    width: 40px;
    height: 44px;
  }

  :deep(.v-icon) {
    color: #fff;
    font-size: 24px;
  }

  &.revenue-icon-red {
    background: transparent;
  }

  &.revenue-icon-energy {
    background: transparent;
  }

  &.revenue-icon-blue {
    background: transparent;
  }
}

.revenue-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.revenue-value {
  font-size: 18px;
  font-weight: 700;
  color: #2E2E2E;
  line-height: 1.2;
  font-family: 'HarmonyOS Sans SC', sans-serif;

  .revenue-unit {
    font-weight: 400;
    color: rgba(46, 46, 46, 0.698); // #2E2E2EB2
  }
}

.revenue-label {
  font-size: 14px;
  color: #8b93aa;
  line-height: 1.2;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.mt-4 {
  margin-top: 16px;
}

.chart-card {
  :deep(.v-card-item) {
    padding: 20px 24px 10px 24px !important;
  }
}

.chart-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .expand-icon {
    width: 16px;
    height: 16px;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.7;
    }
  }
}

.chart-container {
  width: 100%;
  height: 400px;
  min-height: 400px;
}

// 设置图表tooltip的样式 - 参考功率跟踪
::v-deep .chart-tooltip {
  padding: 12px 16px !important;
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.90) !important;
  backdrop-filter: blur(4px);
  border: none !important;
}

::v-deep .chart-tooltip__time {
  margin-bottom: 8px;
  font-family: "PingFang SC";
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #6A727C;
}

::v-deep .chart-tooltip__items-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

::v-deep .chart-tooltip__series {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 4px 8px;
  border-radius: 6px;
}

::v-deep .chart-tooltip__series-name,
::v-deep .chart-tooltip__value {
  font-family: "PingFang SC";
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  margin-left: 4px;
  color: var(--text-icon-font-gy-190-primary, rgba(0, 0, 0, 0.90));
}

::v-deep .chart-tooltip__point {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
  flex-shrink: 0;
}

// 生成报告对话框样式
:deep(.generate-report-dialog) {
  .el-dialog {
    border-radius: 30px;
    overflow: hidden;
  }

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 0;
    overflow: hidden;
  }
}

.generate-report-dialog-content {
  background: linear-gradient(180deg, rgba(248, 252, 255, 1) 0%, rgba(249, 250, 252, 1) 101.29%);
  min-height: 600px;
  max-height: 90vh;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;

  // 固定按钮区域
  .report-actions {
    position: sticky;
    top: 0;
    z-index: 10;
    background: linear-gradient(180deg, rgba(248, 252, 255, 1) 0%, rgba(249, 250, 252, 1) 101.29%);
    padding: 20px 28px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    border-bottom: 1px solid rgba(106, 114, 124, 0.1);
    flex-shrink: 0;
  }

  // 可滚动的内容区域
  .report-preview-wrapper {
    flex: 1;
    overflow-y: auto;
    padding: 28px;
  }

  .report-preview {
    background: #ffffff;
    border-radius: 14px;
    padding: 40px;
    min-height: 800px;

    .report-header {
      margin-bottom: 32px;
    }

    .report-title {
      font-family: 'HarmonyOS Sans SC', sans-serif;
      font-size: 24px;
      font-weight: 500;
      color: #2e2e2e;
    }

    .report-content {
      .report-info-section {
        margin-bottom: 32px;

        .info-row {
          display: flex;
          gap: 32px;
          margin-bottom: 32px;

          .basic-info-box,
          .revenue-info-box {
            flex-grow: 1;
            background: #fafcff;
            border-radius: 18px;
            padding: 20px;
          }

          .section-title {
            font-family: 'HarmonyOS Sans SC', sans-serif;
            font-size: 16px;
            font-weight: 400;
            color: #2e2e2e;
            margin-bottom: 16px;
          }

          .info-content {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px 24px;

            .info-item {
              .info-label {
                font-family: 'HarmonyOS Sans SC', sans-serif;
                font-size: 12px;
                color: rgba(46, 46, 46, 0.7);
                margin-right: 8px;
              }

              .info-value {
                font-family: 'HarmonyOS Sans SC', sans-serif;
                font-size: 12px;
                color: #2e2e2e;
              }
            }
          }

          .revenue-content {
            display: flex;
            gap: 32px;

            .revenue-item {
              display: flex;
              align-items: center;
              gap: 12px;

              .revenue-icon-wrapper {
                width: 40px;
                height: 44px;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                  width: 100%;
                  height: 100%;
                  object-fit: contain;
                }
              }

              .revenue-details {
                .revenue-value {
                  font-family: 'Gotham', sans-serif;
                  font-size: 18px;
                  font-weight: 500;
                  color: #2e2e2e;
                  margin-bottom: 6px;

                  .revenue-unit {
                    font-size: 12px;
                    font-weight: 400;
                    color: rgba(46, 46, 46, 0.698);
                    margin-left: 4px;
                  }
                }

                .revenue-label {
                  font-family: 'HarmonyOS Sans SC', sans-serif;
                  font-size: 12px;
                  color: rgba(46, 46, 46, 0.5);
                }
              }
            }
          }
        }
      }

      .summary-section {
        background: #fffafa;
        border-radius: 18px;
        padding: 20px;

        .section-title {
          font-family: 'HarmonyOS Sans SC', sans-serif;
          font-size: 16px;
          font-weight: 400;
          color: #2e2e2e;
          margin-bottom: 16px;
        }

        .summary-content {
          font-family: 'HarmonyOS Sans SC', sans-serif;
          font-size: 12px;
          color: #000000;
          line-height: 1.6;

          p {
            margin: 0 0 8px 0;

            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }

      .report-charts-section {
        .charts-row {
          display: flex;

          &:last-child {
            margin-bottom: 0;
          }

          .chart-box {
            flex: 1;
            background: #ffffff;
            border-radius: 20px;
            padding: 12px;

            .chart-title {
              font-family: 'HarmonyOS Sans SC', sans-serif;
              font-size: 16px;
              font-weight: 400;
              color: #2e2e2e;
              margin-bottom: 16px;
            }
          }
        }
      }
    }
  }
}

.mt-4 {
  margin-top: 16px;
}

:deep(.el-overlay-dialog) {
  overflow: hidden !important;
}

// 总结建议对话框样式
:deep(.summary-dialog) {
  .v-overlay__content {
    max-width: 90vw;
    max-height: 90vh;
  }
}

.summary-dialog-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.summary-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
  border-bottom: 1px solid rgba(106, 114, 124, 0.1);

  .summary-dialog-title {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 20px;
    font-weight: 400;
    color: #2E2E2E;
  }

  .close-icon {
    cursor: pointer;
    color: rgba(46, 46, 46, 0.70);
    transition: color 0.2s;

    &:hover {
      color: #2E2E2E;
    }
  }
}

.summary-dialog-body {
  flex: 1;
  padding: 24px;
  overflow: auto;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.summary-dialog-content {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: #2e2e2e;
  line-height: 1.8;

  p {
    margin: 12px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// 报告按钮样式
.report-export-btn {
  width: 98px;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 12px !important;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-weight: 400;
  background: #3a68ff !important;
  color: #fff !important;
  text-transform: none !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #3a68ff !important;
    opacity: 0.9;
  }

  &:active {
    background: #3a68ff !important;
    opacity: 0.8;
  }
}

.report-return-btn {
  width: 98px;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 12px !important;
  border: 1px solid rgba(192, 207, 218, 0.3) !important;
  font-family: "HarmonyOS Sans SC", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #2e2e2e !important;
  background: #fff !important;
  text-transform: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f5f5f5 !important;
  }

  &:active {
    background: #ebebeb !important;
  }
}

// 打印时隐藏按钮
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
